import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import { api, formatApiError } from "../lib/api";
import { formatoDinero, aCentavos } from "../lib/money";
import { toast } from "sonner";
import { AlertTriangle, Building2, Stethoscope } from "lucide-react";

// Alta de un gasto (Control financiero, FASE 2 entrega 2).
//
// 🔴 EL DINERO SE ESCRIBE COMO TEXTO Y VIAJA COMO TEXTO. `monto` sale de este formulario
//    tal cual lo tecleó el usuario, validado con `aCentavos` (que es una comprobación de
//    forma, no una conversión para operar). NUNCA se pasa por `parseFloat` ni por `Number`:
//    un importe convertido a float ya perdió la garantía que sostiene el módulo. El único
//    `Number` del camino está dentro de `formatoDinero`, en la última línea antes de pintar.
//
// 🔴 `formatMXN` de lib/api.js NO se usa aquí: redondea los centavos (DT.236).
//
// 🔴 LO QUE NO SE PUEDE CORREGIR DESPUÉS, y la pantalla lo dice con palabras y no con un
//    error de permiso: el grant de UPDATE sobre `gastos` está limitado a la nota y a los
//    campos de anulación. Monto, fecha, categoría y doctor no se editan — se anula el gasto
//    y se rehace. Que el motor lo imponga es el diseño; que el usuario se entere por un
//    42501 sería el fallo.

const CAMPO = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-brand-electric";

export default function GastoModal({ open, onClose, categorias, doctores, hoy, onCreado }) {
  const [monto, setMonto] = useState("");
  const [categoria, setCategoria] = useState("insumos");
  const [doctorId, setDoctorId] = useState("");
  const [fecha, setFecha] = useState(hoy || "");
  const [nota, setNota] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null); // {detail, code}

  useEffect(() => {
    if (!open) return;
    setMonto(""); setCategoria("insumos"); setDoctorId("");
    setFecha(hoy || ""); setNota(""); setError(null); setGuardando(false);
  }, [open, hoy]);

  const centavos = aCentavos(monto);
  const montoValido = centavos !== null && centavos > 0;

  async function guardar() {
    setError(null);
    if (!montoValido) {
      setError({ detail: "Escribe un importe mayor que cero, con dos decimales como máximo.", code: "MONTO_INVALIDO" });
      return;
    }
    setGuardando(true);
    try {
      const { data } = await api.post("/gastos", {
        monto: monto.trim(),          // CADENA, nunca Number
        categoria,
        doctor_id: doctorId || null,
        fecha: fecha || null,
        nota: nota.trim(),
      });
      toast.success("Gasto registrado");
      onCreado?.(data);
      onClose?.();
    } catch (e) {
      // Nunca un catch mudo, y se ramifica por `code`, jamás por el texto (DT.60).
      const code = e?.response?.data?.code || null;
      const detail = formatApiError(e?.response?.data?.detail);
      setError({ detail, code });
    } finally {
      // 🔴 También en el camino de excepción: si el estado de carga sólo se limpiara en el
      //    camino feliz, un error dejaría el botón colgado en "Guardando…" para siempre.
      setGuardando(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={guardando ? () => {} : onClose}
      title="Registrar gasto"
      testid="gasto-modal"
      footer={
        <>
          <button onClick={onClose} disabled={guardando}
                  className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-50">
            Cancelar
          </button>
          <button onClick={guardar} disabled={guardando || !montoValido}
                  data-testid="gasto-guardar"
                  className="px-4 py-2 rounded-lg text-sm font-semibold bg-brand-electric text-white disabled:opacity-50">
            {guardando ? "Guardando…" : "Registrar gasto"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="flex gap-2 p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-800"
               data-testid="gasto-error">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <div>{error.detail}</div>
              {error.code === "FECHA_FUTURA" && (
                <div className="mt-1 text-rose-700">
                  Un gasto se registra cuando ya ocurrió. Si es un pago programado, regístralo el día que salga el dinero.
                </div>
              )}
              {error.code === "DOCTOR_NOT_FOUND" && (
                <div className="mt-1 text-rose-700">Vuelve a elegir el médico de la lista.</div>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Monto</label>
            <input
              className={CAMPO} value={monto} onChange={(e) => setMonto(e.target.value)}
              inputMode="decimal" placeholder="0.00" autoFocus data-testid="gasto-monto"
            />
            <div className="mt-1 text-xs text-slate-500 h-4">
              {monto.trim() === "" ? "" : montoValido ? formatoDinero(monto.trim()) : "Importe no válido"}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Fecha</label>
            <input
              type="date" className={CAMPO} value={fecha} max={hoy || undefined}
              onChange={(e) => setFecha(e.target.value)} data-testid="gasto-fecha"
            />
            <div className="mt-1 text-xs text-slate-500 h-4">No puede ser futura.</div>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Categoría</label>
          <select className={CAMPO} value={categoria} onChange={(e) => setCategoria(e.target.value)}
                  data-testid="gasto-categoria">
            {(categorias || []).map((c) => (
              <option key={c.valor} value={c.valor}>{c.etiqueta}</option>
            ))}
          </select>
          {/* La lista viene del backend a propósito: si el frontend llevara su propia copia,
              serían dos constantes que se desincronizan (el caso de DOCTOR_PLAN_LIMITS). */}
          <div className="mt-1 text-xs text-slate-500">
            Las categorías son fijas para que los reportes se puedan comparar entre periodos.
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">¿De quién es el gasto?</label>
          <select className={CAMPO} value={doctorId} onChange={(e) => setDoctorId(e.target.value)}
                  data-testid="gasto-doctor">
            <option value="">De la clínica</option>
            {(doctores || []).map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          {/* 🔴 La semántica se explica en la pantalla, no sólo en el código: es lo que decide
              a quién se le imputa el dinero en el neto por médico. */}
          <div className="mt-1 flex items-start gap-1.5 text-xs text-slate-500">
            {doctorId ? <Stethoscope className="w-3.5 h-3.5 shrink-0 mt-px" />
                      : <Building2 className="w-3.5 h-3.5 shrink-0 mt-px" />}
            <span>
              {doctorId
                ? "Se descontará del resultado de ese médico."
                : "Gasto general del consultorio: no se descuenta de ningún médico en particular."}
            </span>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Nota (opcional)</label>
          <input className={CAMPO} value={nota} onChange={(e) => setNota(e.target.value)}
                 placeholder="Proveedor, número de ticket, detalle…" data-testid="gasto-nota" />
          {/* Sin comprobante en esta entrega: es una decisión, no un olvido. */}
          <div className="mt-1 text-xs text-slate-500">
            Por ahora no se adjuntan comprobantes. Anota aquí la referencia del ticket o la factura.
          </div>
        </div>

        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
          El monto, la fecha, la categoría y el médico de un gasto <strong>no se editan</strong>.
          Si algo queda mal, se anula el gasto y se vuelve a registrar — el anulado se queda a la
          vista, con su motivo, y deja de sumar.
        </div>
      </div>
    </Modal>
  );
}
